/**
 * Autorización de rutas de portal — núcleo puro consumido por `PermissionRoute`.
 *
 * Por qué existe
 * --------------
 * `PermissionRoute` tenía un `if` escrito a mano por cada portal, y cada uno
 * repetía el mismo bucle sobre `allowedPaths`. Copiar el bloque anterior era el
 * flujo normal para agregar un portal nuevo, y bastaba olvidar el bucle para
 * dejar un gate que ignoraba la base de datos: el submenú aparecía en el
 * sidebar (porque el sidebar sí lee `submenus_permisos`) pero la ruta devolvía
 * 403. Eso fue exactamente lo que pasó con `/admin/portal-embajador`, cuyo gate
 * solo aceptaba rol_id 1/2/23, los nombres 'Embajador'/'Cliente' y una fila en
 * `user_roles`: cualquier otro rol con permiso de lectura en BD (p. ej. Admin
 * Soporte, rol 30) veía el menú y chocaba con "Acceso Denegado".
 *
 * Aquí la autorización es una TABLA declarativa (`PORTAL_GATES`) y una sola
 * función (`decidePortalAccess`). El permiso en BD es siempre una condición
 * suficiente, así que un portal nuevo se autoriza correctamente con solo
 * agregar su prefijo a la tabla, y los tests recorren la tabla completa para
 * garantizar que ningún gate vuelva a ignorar la BD.
 *
 * Patrón "coarse"
 * ---------------
 * Muchas rutas de portal (`expediente/:id`, `cases/:id`, `bandeja-ejecucion`,
 * las pestañas de Precios…) no tienen submenú propio y nunca aparecen en
 * `allowedPaths`. Por eso basta tener permiso de lectura sobre CUALQUIER
 * submenú bajo el prefijo del portal para habilitar todas sus rutas.
 */

/** Roles de banco detectados por NOMBRE: sus ids difieren entre ambientes. */
export const isRolDeBanco = (rolNombre: string | null | undefined): boolean => {
  const n = (rolNombre ?? '').trim().toLowerCase();
  return n === 'banco' || n.startsWith('operador banco') || n.startsWith('supervisor banco');
};

/**
 * Roles de notaría detectados por NOMBRE, mismo criterio que usa
 * AppNotariaUsuarios para listarlos (`nombre ILIKE '%notari%'`): en dev solo
 * existe "Notario" (id 6), pero el DDL del portal agrega "Colaborador notaría"
 * y los ids no coinciden entre ambientes.
 */
export const isRolDeNotaria = (rolNombre: string | null | undefined): boolean =>
  (rolNombre ?? '').trim().toLowerCase().includes('notari');

export interface PortalGate {
  /** Prefijo de ruta que cubre este gate. */
  prefix: string;
  /**
   * rol_id que entran SIN fila en `submenus_permisos`. Son bypass históricos:
   * varios roles de portal dependen de ellos y pueden no tener permisos propios
   * en algún ambiente. Nunca son la única vía de entrada — el permiso en BD
   * también alcanza.
   */
  rolesBypass?: number[];
  /** Igual que `rolesBypass` pero por rol_nombre (ids que varían por ambiente). */
  nombresBypass?: string[];
  /**
   * Rutas de administración del portal que exigen permiso de lectura EXACTO
   * sobre esa vista en BD, sin coarse ni bypass por rol.
   */
  adminPaths?: string[];
  /**
   * true si el gate acepta además la señal de `useHasEmbajadorRole`
   * (rol dual vía `user_roles`, sin cambiar el rol principal del usuario).
   */
  aceptaRolEmbajadorDual?: boolean;
  /**
   * true si el gate acepta además `hasSocioRole` de `useRolesAdicionales`
   * (rol 36 Socio como rol ADICIONAL en `user_roles`, que la BD asigna sola a
   * los fideicomisarios; el rol principal suele ser Cliente 23).
   */
  aceptaRolSocioDual?: boolean;
  /**
   * Permiso EXACTO por vista en vez del patrón coarse: cada ruta exige lectura
   * sobre el submenú registrado más específico que la cubre (las subrutas como
   * `entregas/:id` heredan el de `entregas`). Las rutas sin submenú propio se
   * resuelven con `alias`; una ruta que no resuelve a ningún submenú se niega.
   */
  exacto?: boolean;
  /** Ruta sin submenú propio → vista cuyo permiso hereda (solo con `exacto`). */
  alias?: Record<string, string>;
}

/**
 * OJO: agregar un portal aquí es lo único necesario para que su permiso en BD
 * funcione. No volver a escribir gates a mano en `PermissionRoute`.
 */
export const PORTAL_GATES: PortalGate[] = [
  { prefix: '/admin/portal-estructura-comisiones', rolesBypass: [1, 2] },
  {
    // Exacto (2026-10-22, auditoría de seguridad): con el coarse, leer cualquier submenú abría
    // todas las rutas (Avisos y Ofertas incluidas) y el bypass de rol 2 entraba sin fila en BD.
    // Las rutas viejas que solo redirigen heredan el permiso de su destino.
    prefix: '/admin/portal-productos',
    exacto: true,
    alias: {
      '/admin/portal-productos/resumen': '/admin/portal-productos/productos',
      '/admin/portal-productos/analisis': '/admin/portal-productos/productos',
      '/admin/portal-productos/cartera': '/admin/portal-productos/cuentas-cobranza',
      '/admin/portal-productos/configuracion-tickets': '/admin/portal-productos/tickets',
    },
  },
  {
    // Exacto: con el coarse, quien tenía una sola vista del portal (p. ej. Todos)
    // abría Pipelines y Analítica por URL. Sin bypass por rol: el permiso lo da la BD.
    prefix: '/admin/portal-tickets',
    exacto: true,
    alias: {
      '/admin/portal-tickets/mis-tickets': '/admin/portal-tickets/todos',
      '/admin/portal-tickets/sin-asignar': '/admin/portal-tickets/todos',
      '/admin/portal-tickets/pipeline': '/admin/portal-tickets/todos',
      '/admin/portal-tickets/configuracion/equipo': '/admin/portal-tickets/analitica',
      '/admin/portal-tickets/configuracion': '/admin/portal-tickets/pipelines',
    },
  },
  { prefix: '/admin/portal-personal', rolesBypass: [1, 2] },
  {
    // El rol Cliente (23) entra por rol, sin fila en `user_roles`: todo cliente
    // puede referir con su misma cuenta.
    prefix: '/admin/portal-embajador',
    rolesBypass: [1, 2, 23],
    nombresBypass: ['Embajador', 'Cliente'],
    aceptaRolEmbajadorDual: true,
  },
  {
    // Inventario del portal: prefijo MÁS LARGO que el del portal, así que gana
    // en `findPortalGate` y NO hereda el bypass del rol Cliente (23).
    //
    // Sin esta entrada, los 622 clientes que hoy entran al portal a referir
    // verían el inventario completo de todos los proyectos con precios: la
    // vista se pidió para embajadores, no para compradores finales. Un cliente
    // que además sea embajador sí pasa, por `aceptaRolEmbajadorDual`.
    //
    // Los roles internos (Super Admin, Admin de Proyecto, Admin Soporte…) entran
    // por su permiso en `submenus_permisos` sobre esta misma ruta.
    prefix: '/admin/portal-embajador/inventario',
    rolesBypass: [1, 2],
    nombresBypass: ['Embajador'],
    aceptaRolEmbajadorDual: true,
  },
  // Módulo de Precios: el submenú registrado es '/admin/inventario/precios',
  // pero sus pestañas reales (tabla, motor, calibracion, escenarios/*,
  // auditoria/*) no tienen submenú propio.
  { prefix: '/admin/inventario/precios' },
  // Mercado secundario (menú 42, antes "Reventas"; épica #1396): submenús registrados
  // '/admin/reventas/pipeline' (F1), '/admin/reventas/agenda' (F2), '/admin/reventas/ofertas' (F3),
  // '/admin/reventas/comisiones' y '/admin/reventas/cumplimiento' (F4), e '/admin/reventas/inventario',
  // '/admin/reventas/libro' y '/admin/reventas/configuracion' (D-61). El expediente (`expediente/:id`)
  // y las pestañas del libro (`libro/tabla`...) no tienen submenú propio y heredan el permiso del grupo.
  { prefix: '/admin/reventas' },
  { prefix: '/admin/portal-administracion' },
  { prefix: '/admin/legal-flow' },
  {
    // Exacto (2026-10-22, auditoría de seguridad): mismo caso que Escrituración; Regularización
    // y Avisos (solo roles 1 y 12 en BD) se abrían por URL con cualquier permiso del portal.
    prefix: '/admin/portal-cobranza',
    exacto: true,
    alias: {
      '/admin/portal-cobranza/complementos': '/admin/portal-cobranza/productos',
      '/admin/portal-cobranza/ceps-pendientes': '/admin/portal-cobranza/relacion-pagos',
      '/admin/portal-cobranza/configuracion-tickets': '/admin/portal-cobranza/tickets',
    },
  },
  {
    // Exacto: el rol 14 (Representante de empresa dueña) tiene solo Entregas y
    // Relación de Pagos, y con el coarse entraba a las 23 vistas del portal.
    prefix: '/admin/portal-escrituracion',
    exacto: true,
    // Unidades por escriturar y Configuración de checklist tienen submenú propio desde
    // 20261023233517. App notaría y App jurídico siguen tomando el permiso de su vista.
    alias: {
      '/admin/portal-escrituracion/app-notaria': '/admin/portal-escrituracion/notarias',
      '/admin/portal-escrituracion/app-juridico': '/admin/portal-escrituracion/demandas',
    },
  },
  {
    // Exacto: con el coarse, el Operador abría Auditoría, Configuración, Titularidad,
    // Amenidades y Presupuesto por URL sin tener su submenú.
    prefix: '/admin/portal-condominio',
    exacto: true,
    alias: {
      '/admin/portal-condominio/cargos': '/admin/portal-condominio/cobranza',
      '/admin/portal-condominio/pagos': '/admin/portal-condominio/cobranza',
    },
  },
  { prefix: '/admin/portal-crm' },
  {
    // Portal de Socios (fideicomisarios). Entra el rol 36 como principal o como
    // rol adicional en `user_roles`; los internos (Super Admin…) por permiso en BD.
    prefix: '/admin/portal-socio',
    rolesBypass: [36],
    aceptaRolSocioDual: true,
  },
  {
    // Captura del portal (cronograma, avance, noticias, repositorio): prefijo
    // MÁS LARGO, así que gana y NO hereda el bypass del rol Socio ni la señal
    // dual. Solo abre con permiso en BD sobre esta vista (hoy, rol 1).
    prefix: '/admin/portal-socio/configuracion',
  },
  {
    // Exacto en todo el portal: con el coarse, un rol con una sola vista abría Notarías,
    // Indicadores y Soporte por URL (Ejecutivos, Convenios y Clientes ya eran exactos,
    // ticket #1226). Sin bypass por nombre de rol: el permiso lo da la BD.
    prefix: '/admin/portal-bancos',
    exacto: true,
    adminPaths: ['/admin/portal-bancos/ejecutivos', '/admin/portal-bancos/convenios', '/admin/portal-bancos/clientes'],
    alias: {
      '/admin/portal-bancos/pipeline': '/admin/portal-bancos/proceso',
      '/admin/portal-bancos/tablero': '/admin/portal-bancos/indicadores',
      '/admin/portal-bancos/parametros': '/admin/portal-bancos/convenios',
      '/admin/portal-bancos/bandeja': '/admin/portal-bancos/solicitudes',
      '/admin/portal-bancos/broker': '/admin/portal-bancos/clientes',
      '/admin/portal-bancos/equipo': '/admin/portal-bancos/ejecutivos',
      '/admin/portal-bancos/bancos': '/admin/portal-bancos/convenios',
    },
  },
];

export interface PortalAccessContext {
  rolId: number | null | undefined;
  rolNombre: string | null | undefined;
  /** `allowedPaths` de `useAllowedMenus`. Para Super Admin es el wildcard {'*'}. */
  allowedPaths: Set<string>;
  isSuperAdmin: boolean;
  /** `useHasEmbajadorRole`: null mientras consulta `user_roles`. */
  hasEmbajadorRole: boolean | null;
  /** `useRolesAdicionales().hasSocioRole`: null mientras consulta `user_roles`. */
  hasSocioRole: boolean | null;
  /**
   * Rutas con submenú activo registrado (`enabledPaths` de `useAllowedMenus`).
   * Las usan los gates `exacto` para saber qué submenú cubre una subruta.
   */
  rutasRegistradas?: Set<string>;
}

/**
 * 'allow'   → renderizar la ruta.
 * 'deny'    → mandar a /admin/access-denied.
 * 'pending' → falta una señal asíncrona; mostrar spinner, no juzgar todavía.
 * null      → la ruta no pertenece a ningún gate; sigue el flujo normal.
 */
export type PortalAccessDecision = 'allow' | 'deny' | 'pending' | null;

/** ¿Hay algún permiso de lectura bajo `prefix`? (`allowedPaths` es un Set: iterar.) */
export function tieneAccesoBajo(allowedPaths: Set<string>, prefix: string): boolean {
  for (const p of allowedPaths) {
    if (p.startsWith(prefix)) return true;
  }
  return false;
}

const cubre = (ruta: string, pathname: string) => pathname === ruta || pathname.startsWith(`${ruta}/`);

/**
 * Vista cuyo permiso exige `pathname` en un gate `exacto`: el alias si lo
 * tiene; si no, el submenú registrado más específico bajo el prefijo del gate.
 * null si ninguna vista la cubre.
 */
export function vistaRequerida(
  gate: PortalGate,
  pathname: string,
  rutasRegistradas: Set<string> | undefined,
): string | null {
  const clean = pathname.replace(/\/+$/, '') || pathname;
  for (const [ruta, destino] of Object.entries(gate.alias ?? {})) {
    if (cubre(ruta, clean)) return destino;
  }
  let mejor: string | null = null;
  rutasRegistradas?.forEach((ruta) => {
    if (!ruta.startsWith(`${gate.prefix}/`) || !cubre(ruta, clean)) return;
    if (!mejor || ruta.length > mejor.length) mejor = ruta;
  });
  return mejor;
}

/**
 * ¿La sesión puede abrir esta ruta de portal? Para el sidebar: misma decisión
 * que `PermissionRoute`, sin el estado 'pending'.
 */
export function puedeAbrirRutaPortal(pathname: string, ctx: PortalAccessContext): boolean {
  return decidePortalAccess(pathname, ctx) === 'allow';
}

/**
 * En un gate `exacto`, primera vista con permiso bajo el mismo portal: a dónde
 * mandar a quien abre una vista del portal sin permiso (p. ej. el dashboard por
 * un enlace guardado) en vez de dejarlo en "Acceso denegado". null si no hay.
 */
export function vistaAlternativaPortal(pathname: string, allowedPaths: Set<string>): string | null {
  const gate = findPortalGate(pathname);
  if (!gate?.exacto) return null;
  for (const ruta of allowedPaths) {
    if (ruta.startsWith(`${gate.prefix}/`) && ruta !== pathname) return ruta;
  }
  return null;
}

/** Gate más específico que cubre la ruta (prefijo más largo gana). */
export function findPortalGate(pathname: string): PortalGate | null {
  let mejor: PortalGate | null = null;
  for (const gate of PORTAL_GATES) {
    if (!pathname.startsWith(gate.prefix)) continue;
    if (!mejor || gate.prefix.length > mejor.prefix.length) mejor = gate;
  }
  return mejor;
}

export function decidePortalAccess(
  pathname: string,
  ctx: PortalAccessContext,
): PortalAccessDecision {
  const gate = findPortalGate(pathname);
  if (!gate) return null;

  // Super Admin ya pasó el gate de vistas apagadas en PermissionRoute; su
  // allowedPaths es el wildcard {'*'} y no haría match por prefijo.
  if (ctx.isSuperAdmin) return 'allow';

  // Administración del portal: permiso EXPLÍCITO sobre esa vista, sin coarse.
  const adminPath = gate.adminPaths?.find((p) => pathname.startsWith(p));
  if (adminPath) {
    return ctx.allowedPaths.has(adminPath) ? 'allow' : 'deny';
  }

  // Exacto: solo el permiso de la vista que cubre la ruta, sin coarse ni bypass.
  if (gate.exacto) {
    const vista = vistaRequerida(gate, pathname, ctx.rutasRegistradas);
    return vista && ctx.allowedPaths.has(vista) ? 'allow' : 'deny';
  }

  // El permiso en BD es SIEMPRE suficiente: es la señal que pinta el menú, así
  // que si el sidebar lo muestra, la ruta tiene que abrir.
  if (tieneAccesoBajo(ctx.allowedPaths, gate.prefix)) return 'allow';

  if (gate.rolesBypass?.includes(ctx.rolId ?? -1)) return 'allow';
  if (gate.nombresBypass?.includes(ctx.rolNombre ?? '')) return 'allow';

  if (gate.aceptaRolEmbajadorDual) {
    if (ctx.hasEmbajadorRole === null) return 'pending';
    if (ctx.hasEmbajadorRole) return 'allow';
  }

  if (gate.aceptaRolSocioDual) {
    if (ctx.hasSocioRole === null) return 'pending';
    if (ctx.hasSocioRole) return 'allow';
  }

  return 'deny';
}

/**
 * Rutas de submenú renombradas para que coincidan con su nombre visible.
 * La `vista_front_end` en BD ya es la nueva; la vieja solo sobrevive como
 * redirect para enlaces guardados. `PermissionRoute` redirige ANTES de evaluar
 * permisos, así que quien valida el acceso es siempre la ruta destino (la
 * vieja ya no existe en `submenus` y caería en "Acceso denegado" o en otra
 * vista del portal). Los `<Route>` de `App.tsx` con `<Navigate>` cubren lo mismo
 * dentro del router.
 */
const PEC = '/admin/portal-estructura-comisiones';
export const RUTAS_RENOMBRADAS: Readonly<Record<string, string>> = {
  '/admin/portal-inmobiliaria/configuracion': '/admin/portal-inmobiliaria/cuenta',
  '/admin/servicios-clientes': '/admin/servicios-proveedores',
  '/admin/portal-bancos/pipeline': '/admin/portal-bancos/proceso',
  '/admin/portal-bancos/tablero': '/admin/portal-bancos/indicadores',
  [`${PEC}/structure`]: `${PEC}/roles-sueldos`,
  [`${PEC}/directorio`]: `${PEC}/roles-sueldos`,
  [`${PEC}/broker-incentives`]: `${PEC}/incentivos-dinamicos`,
  [`${PEC}/dist-simulator`]: `${PEC}/distribucion`,
  [`${PEC}/monthly-flow`]: `${PEC}/flujo-comercial`,
  [`${PEC}/results`]: `${PEC}/financieros`,
  [`${PEC}/compensation`]: `${PEC}/costos-comerciales`,
  [`${PEC}/broker-calc`]: `${PEC}/simulador-ingresos`,
  [`${PEC}/comm-simulator`]: `${PEC}/comparador`,
  // La ruta del submenú lleva el nombre del menú (2026-10-20).
  '/admin/portal-bancos/bandeja': '/admin/portal-bancos/solicitudes',
  '/admin/portal-bancos/broker': '/admin/portal-bancos/clientes',
  // Ficha del cliente: los correos ya enviados al broker traen el enlace viejo.
  '/admin/portal-bancos/broker/clientes': '/admin/portal-bancos/clientes',
  '/admin/portal-bancos/equipo': '/admin/portal-bancos/ejecutivos',
  '/admin/portal-bancos/bancos': '/admin/portal-bancos/convenios',
  '/admin/legal-flow/requests': '/admin/legal-flow/solicitudes',
  '/admin/legal-flow/requests/new': '/admin/legal-flow/solicitudes/nueva',
  '/admin/legal-flow/templates': '/admin/legal-flow/plantillas',
  '/admin/legal-flow/archived': '/admin/legal-flow/archivados',
  '/admin/legal-flow/notifications': '/admin/legal-flow/notificaciones',
  '/admin/material-propuestas': '/admin/revision-material',
  // El Libro de Reventa salió de Inventarios > Precios a Mercado secundario (D-61, ticket #1396).
  '/admin/inventario/precios/reventa': '/admin/reventas/libro',
};

/**
 * Ruta nueva si `pathname` (sin barra final) es una ruta renombrada o cuelga de
 * una (`/vieja/:id` → `/nueva/:id`); si no, null. Gana la coincidencia exacta y,
 * entre subrutas, la ruta vieja más larga (`broker/clientes/46` → `clientes/46`).
 */
export const rutaRenombrada = (pathname: string): string | null => {
  const limpia = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const exacta = RUTAS_RENOMBRADAS[limpia];
  if (exacta) return exacta;
  let vieja: string | null = null;
  for (const ruta of Object.keys(RUTAS_RENOMBRADAS)) {
    if (limpia.startsWith(`${ruta}/`) && (!vieja || ruta.length > vieja.length)) vieja = ruta;
  }
  return vieja ? RUTAS_RENOMBRADAS[vieja] + limpia.slice(vieja.length) : null;
};
